import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Database, Cloud, Shield, FileText, CheckCircle2, TrendingUp, Clock, Zap, Server } from 'lucide-react';
import SEOHead from '../../components/SEOHead';

const technologies = [
  { name: 'Azure SQL', icon: Database },
  { name: 'AWS Lambda', icon: Cloud },
  { name: 'Terraform', icon: Server },
  { name: 'GitLab CI/CD', icon: Shield },
  { name: 'Tableau', icon: FileText },
  { name: 'AWS Glue & Athena', icon: Database }
];

const keyMetrics = [
  {
    icon: CheckCircle2,
    value: 'Zero',
    label: 'Data Loss',
    description: 'Across full Azure SQL migration with schema validation and batch processing'
  },
  {
    icon: Clock,
    value: '17+',
    label: 'Month Engagement',
    description: 'Sustained enterprise platform delivery with continuous value'
  },
  {
    icon: Zap,
    value: '15+',
    label: 'Services Automated',
    description: 'Across a unified cloud-native AWS data platform'
  },
  {
    icon: TrendingUp,
    value: 'Full',
    label: 'Terraform IaC',
    description: 'Version-controlled infrastructure across all AWS services'
  }
];

const highlights = [
  {
    category: 'Migration Excellence',
    items: [
      'Completed full Azure SQL migration with zero data loss through phased schema validation, field mapping correction, and transaction management',
      'Implemented full Terraform IaC across all AWS services eliminating all manual infrastructure provisioning',
      'Deployed GitLab CI/CD pipeline for automated, version-controlled infrastructure and application deployments'
    ]
  },
  {
    category: 'Platform Modernization',
    items: [
      'Automated data pipelines across 15+ enterprise services using AWS Lambda, Glue, and EventBridge',
      'Built REST API and Google Sheets API integrations replacing all manual data handoff processes',
      'Delivered Tableau dashboards with Privacy Impact Assessment compliance embedded in reporting workflows'
    ]
  }
];

const challenges = [
  {
    title: 'Zero-Tolerance Database Migration',
    icon: Database,
    problem: 'Migrating a production enterprise database to Azure SQL with no tolerance for data loss required precise schema mapping, type alignment, and reliable rollback capability without halting operations.',
    solution: 'Implemented phased batch migration with schema validation gates, automated error handling, and transaction management — each batch validated against source before the next was committed.',
    impact: 'Zero data loss confirmed across the full production migration'
  },
  {
    title: 'Fragmented Cloud Services with No Governance',
    icon: Cloud,
    problem: '15+ services operated independently with no unified infrastructure layer, making deployments inconsistent, ungovernable, and impossible to audit or replicate across environments.',
    solution: 'Rebuilt the entire cloud infrastructure using Terraform IaC with GitLab CI/CD, enabling version-controlled, repeatable deployments with a complete change history across all services.',
    impact: 'Full Terraform IaC coverage with automated deployment governance across all 15+ services'
  },
  {
    title: 'Manual Data Pipelines with No Monitoring',
    icon: Server,
    problem: 'Data handoffs between enterprise services relied on manual exports, spreadsheets, and ad hoc scripts with no scheduling, monitoring, or alerting — failures went undetected until downstream impact.',
    solution: 'Built automated AWS Glue and Lambda pipelines with EventBridge scheduling and CloudWatch monitoring, replacing all manual processes with observable, alerting-enabled automation.',
    impact: '15+ services fully automated with real-time monitoring and failure alerting'
  },
  {
    title: 'Compliance and Privacy Governance Gaps',
    icon: Shield,
    problem: 'No formal Privacy Impact Assessment process existed for data handling, and API credentials were managed inconsistently across services — creating regulatory risk and security exposure.',
    solution: 'Embedded PIA compliance into Tableau reporting workflows and implemented automated credential rotation for all API integrations with centralised secrets management.',
    impact: 'Full compliance governance maintained across a 17+ month production engagement'
  }
];

const CloudPlatformMigration = () => {
  return (
    <>
      <SEOHead
        title="Enterprise Database Migration & Cloud Data Platform Case Study | Agility AI"
        description="How Agility migrated a legacy enterprise database to Azure SQL with zero data loss and automated 15+ services across a cloud-native AWS platform over a 17-month engagement using Terraform IaC."
        url="https://www.agilitytech.ai/case-studies/cloud-platform-migration"
        type="article"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Enterprise Database Migration & Cloud Data Platform",
          "description": "How Agility migrated a legacy enterprise database to Azure SQL with zero data loss and automated 15+ services across a cloud-native AWS platform over a 17-month engagement.",
          "author": { "@type": "Organization", "name": "Agility" },
          "publisher": {
            "@type": "Organization",
            "name": "Agility",
            "logo": { "@type": "ImageObject", "url": "https://www.agilitytech.ai/agility hex fonts.png" }
          },
          "datePublished": "2025-10-01",
          "dateModified": "2026-05-01",
          "mainEntityOfPage": { "@type": "WebPage", "@id": "https://www.agilitytech.ai/case-studies/cloud-platform-migration" }
        }}
      />
      <div className="pt-20 bg-black">
        {/* Hero Section */}
        <section className="relative py-24 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&q=80"
              alt="Cloud Platform Migration Banner"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
          </div>
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-4xl">
              <div className="inline-flex items-center px-4 py-2 bg-teal-500/10 rounded-full border border-teal-500/20 mb-6">
                <span className="text-teal-400 text-sm font-medium">Technology & Consulting</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Enterprise Database Migration & Cloud Data Platform
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed max-w-3xl">
                Migrating a legacy enterprise database to Azure SQL with zero data loss and building a cloud-native AWS automation platform across 15+ enterprise services over a 17-month engagement.
              </p>
            </div>
          </div>
        </section>

        {/* Key Metrics Section */}
        <section className="py-16 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {keyMetrics.map((metric, index) => (
                <div key={index} className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6
                  border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300 text-center">
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <metric.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <div className="text-3xl font-bold text-white mb-2">{metric.value}</div>
                  <div className="text-teal-400 font-medium mb-2">{metric.label}</div>
                  <p className="text-gray-400 text-sm">{metric.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Project Details */}
        <section className="py-12 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Project Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Industry</h3>
                <p className="text-white">Technology & Consulting</p>
              </div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Region</h3>
                <p className="text-white">Global</p>
              </div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Project Size</h3>
                <p className="text-white">Enterprise — Multi-Service Platform</p>
              </div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Time Frame</h3>
                <p className="text-white">17+ Month Engagement</p>
              </div>
            </div>
          </div>
        </section>

        {/* Technologies */}
        <section className="py-12 bg-gray-900 border-t border-gray-800">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-xl font-bold text-white mb-6 text-center">Technology Stack</h3>
            <div className="flex flex-wrap justify-center gap-6">
              {technologies.map((tech, index) => (
                <div key={index} className="flex items-center space-x-3 bg-gray-800/30 rounded-lg px-4 py-3 border border-gray-700/30">
                  <tech.icon className="h-6 w-6 text-teal-400" />
                  <span className="text-gray-300 font-medium">{tech.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Overview Section */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-white mb-8">The Challenge</h2>
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                    <FileText className="h-6 w-6 text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-4">Legacy Database and Disconnected Cloud Services</h3>
                    <p className="text-xl text-gray-300 leading-relaxed">
                      An enterprise technology client operating across 15+ internal services carried significant technical debt from a legacy on-premises database, disconnected cloud infrastructure, and manual data pipeline processes with no monitoring or governance layer. Data handoffs between services relied on ad hoc scripts and manual exports. There was no unified infrastructure-as-code layer, compliance governance was inconsistent, and the absence of CI/CD automation meant deployments were slow, risky, and undocumented.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="py-20 bg-black border-t border-gray-800">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-white mb-8 text-center">Cloud-Native Enterprise Platform</h2>
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50 mb-12">
                <div className="flex items-start space-x-4 mb-8">
                  <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle2 className="h-6 w-6 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-4">Cloud-Native Enterprise Platform</h3>
                    <p className="text-xl text-gray-300 leading-relaxed">
                      We delivered a complete platform transformation — migrating the production database to Azure SQL with zero data loss, rebuilding the entire cloud infrastructure under Terraform IaC, and automating all 15+ enterprise service data pipelines through AWS Lambda, Glue, and EventBridge. Over 17+ months of sustained engagement, every manual process was replaced with an observable, governed, version-controlled automation — with Privacy Impact Assessment compliance built into the reporting layer.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {highlights.map((category, index) => (
                  <div key={index} className="bg-gray-900/30 backdrop-blur-sm rounded-xl p-6 border border-gray-800/30">
                    <h4 className="text-lg font-bold text-teal-400 mb-4">{category.category}</h4>
                    <div className="space-y-4">
                      {category.items.map((item, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <CheckCircle2 className="flex-shrink-0 h-5 w-5 text-green-400 mt-1" />
                          <p className="text-gray-300 text-sm leading-relaxed">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Challenges & Solutions */}
        <section className="py-20 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">Challenges & Solutions</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {challenges.map((challenge, index) => (
                <div key={index} className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8
                  border border-gray-700/50 hover:bg-gray-800/70 transition-all duration-300">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center">
                      <challenge.icon className="h-6 w-6 text-teal-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">{challenge.title}</h3>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-semibold text-red-400 mb-3 flex items-center">
                        <span className="w-2 h-2 bg-red-400 rounded-full mr-3"></span>
                        Problem
                      </h4>
                      <p className="text-gray-300 leading-relaxed pl-5">{challenge.problem}</p>
                    </div>

                    <div>
                      <h4 className="text-lg font-semibold text-green-400 mb-3 flex items-center">
                        <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                        Solution
                      </h4>
                      <p className="text-gray-300 leading-relaxed pl-5">{challenge.solution}</p>
                    </div>

                    <div className="bg-teal-500/10 rounded-lg p-4 border border-teal-500/20">
                      <h4 className="text-sm font-semibold text-teal-400 mb-2">Impact</h4>
                      <p className="text-teal-300 font-medium">{challenge.impact}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-3xl mx-auto">
              <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Cloud className="h-8 w-8 text-teal-400" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-6">
                Ready to Modernize Your Cloud Infrastructure?
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Contact our cloud engineering team to discover how a fully governed, IaC-driven platform can transform your enterprise data operations.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4
                  rounded-lg text-lg font-medium hover:bg-teal-400 transition-all duration-300 group
                  shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <span>Get Started Today</span>
                <ArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CloudPlatformMigration;
